import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../../config';
import './AdminCustomers.css';

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCustomers(data.data || []);
        } else {
          setError(data.message);
        }
      } else {
        setError('Failed to load customers');
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError('Error loading customers');
    } finally {
      setLoading(false);
    }
  };

  const updateCustomerRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ role: newRole })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCustomers(customers.map(customer => 
            customer.id === userId ? { ...customer, role: newRole } : customer
          ));
          alert('Customer role updated successfully');
        } else {
          alert(data.message);
        }
      } else {
        alert('Failed to update customer role');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      alert('Error updating customer role');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'admin': { color: '#e74c3c', text: 'Admin' },
      'customer': { color: '#3498db', text: 'Customer' }
    };
    const config = roleConfig[role] || { color: '#95a5a6', text: role };
    return <span className="role-badge" style={{ backgroundColor: config.color }}>{config.text}</span>;
  };

  // Filter customers
  const filteredCustomers = customers.filter(customer => {
    if (filterRole !== 'all' && customer.role !== filterRole) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return customer.username?.toLowerCase().includes(searchLower) ||
             customer.email?.toLowerCase().includes(searchLower) ||
             customer.first_name?.toLowerCase().includes(searchLower) ||
             customer.last_name?.toLowerCase().includes(searchLower) ||
             customer.phone?.includes(searchTerm);
    }
    return true;
  });

  // Sort customers
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    if (sortBy === 'created_at') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    if (sortBy === 'order_count') {
      aVal = a.order_count || 0;
      bVal = b.order_count || 0;
    }
    
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = sortedCustomers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return <div className="admin-customers-loading">Loading customers...</div>;
  }

  if (error) {
    return <div className="admin-customers-error">{error}</div>;
  }

  return (
    <div className="admin-customers">
      <div className="admin-customers-header">
        <h1>Customers Management</h1>
        <div className="admin-customers-filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="role-filter">
            <option value="all">All Roles</option>
            <option value="customer">Customers</option>
            <option value="admin">Admins</option>
          </select>
          <select 
            value={itemsPerPage} 
            onChange={(e) => setItemsPerPage(Number(e.target.value))} 
            className="items-per-page"
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      </div>

      <div className="admin-customers-stats">
        <div className="stat-card">
          <span className="stat-label">Total Users</span>
          <span className="stat-value">{customers.length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Customers</span>
          <span className="stat-value">{customers.filter(c => c.role === 'customer').length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Admins</span>
          <span className="stat-value">{customers.filter(c => c.role === 'admin').length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">New This Month</span>
          <span className="stat-value">
            {customers.filter(c => {
              const createdDate = new Date(c.created_at);
              const now = new Date();
              return createdDate.getMonth() === now.getMonth() && 
                     createdDate.getFullYear() === now.getFullYear();
            }).length}
          </span>
        </div>
      </div>

      <div className="admin-customers-table-container">
        {filteredCustomers.length === 0 ? (
          <div className="no-customers">No customers found</div>
        ) : (
          <>
            <table className="customers-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('id')} className="sortable">
                    ID {getSortIcon('id')}
                  </th>
                  <th onClick={() => handleSort('first_name')} className="sortable">
                    Name {getSortIcon('first_name')}
                  </th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th onClick={() => handleSort('role')} className="sortable">
                    Role {getSortIcon('role')}
                  </th>
                  <th onClick={() => handleSort('order_count')} className="sortable">
                    Orders {getSortIcon('order_count')}
                  </th>
                  <th onClick={() => handleSort('created_at')} className="sortable">
                    Joined {getSortIcon('created_at')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentCustomers.map(customer => (
                  <React.Fragment key={customer.id}>
                    <tr className={selectedCustomer === customer.id ? 'expanded-row' : ''}>
                      <td className="customer-id">#{customer.id}</td>
                      <td>
                        <div className="customer-name-cell">
                          <div className="customer-avatar-small">
                            {customer.first_name?.charAt(0) || customer.username?.charAt(0) || 'U'}
                            {customer.last_name?.charAt(0) || ''}
                          </div>
                          <div>
                            <div className="customer-fullname">
                              {customer.first_name} {customer.last_name}
                            </div>
                            <div className="customer-username">@{customer.username}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="customer-email">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                          </svg>
                          {customer.email}
                        </div>
                      </td>
                      <td>
                        {customer.phone ? (
                          <div className="customer-phone">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8 10a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                            {customer.phone}
                          </div>
                        ) : (
                          <span className="no-data">—</span>
                        )}
                      </td>
                      <td>{getRoleBadge(customer.role)}</td>
                      <td className="order-count">{customer.order_count || 0}</td>
                      <td>{formatDate(customer.created_at)}</td>
                      <td className="actions-cell">
                        <button 
                          className="action-icon view"
                          onClick={() => setSelectedCustomer(selectedCustomer === customer.id ? null : customer.id)}
                          title="View Details"
                        >
                          {selectedCustomer === customer.id ? '▲' : '▼'}
                        </button>
                        <select
                          value={customer.role}
                          onChange={(e) => updateCustomerRole(customer.id, e.target.value)}
                          className="role-select-mini"
                          title="Change Role"
                        >
                          <option value="customer">Customer</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                    </tr>
                    {selectedCustomer === customer.id && (
                      <tr className="details-row">
                        <td colSpan="8">
                          <div className="customer-details-expanded">
                            <div className="details-grid">
                              <div className="detail-group">
                                <h4>Account Information</h4>
                                <div className="detail-row">
                                  <span className="detail-label">User ID:</span>
                                  <span className="detail-value">{customer.id}</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">Username:</span>
                                  <span className="detail-value">{customer.username}</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">Full Name:</span>
                                  <span className="detail-value">{customer.first_name} {customer.last_name}</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">Email:</span>
                                  <span className="detail-value">{customer.email}</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">Phone:</span>
                                  <span className="detail-value">{customer.phone || 'Not provided'}</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">Member Since:</span>
                                  <span className="detail-value">{formatDate(customer.created_at)}</span>
                                </div>
                              </div>
                              <div className="detail-group">
                                <h4>Statistics</h4>
                                <div className="detail-row">
                                  <span className="detail-label">Total Orders:</span>
                                  <span className="detail-value">{customer.order_count || 0}</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">Total Spent:</span>
                                  <span className="detail-value">₹{customer.total_spent?.toFixed(2) || '0.00'}</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">Last Order:</span>
                                  <span className="detail-value">{customer.last_order_date ? formatDate(customer.last_order_date) : 'No orders'}</span>
                                </div>
                              </div>
                              <div className="detail-group">
                                <h4>Quick Actions</h4>
                                <div className="action-buttons-group">
                                  <Link to={`/admin/orders?user=${customer.id}`} className="action-link">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <rect x="2" y="4" width="20" height="16" rx="2"/>
                                      <path d="M8 8h8M8 12h6M8 16h4"/>
                                    </svg>
                                    View Orders
                                  </Link>
                                  <Link to={`/admin/customers/${customer.id}/edit`} className="action-link">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"/>
                                    </svg>
                                    Edit Profile
                                  </Link>
                                  <button className="action-link">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                      <polyline points="22,6 12,13 2,6"/>
                                    </svg>
                                    Send Email
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(1)} 
                  disabled={currentPage === 1}
                  className="page-btn"
                >
                  «
                </button>
                <button 
                  onClick={() => setCurrentPage(currentPage - 1)} 
                  disabled={currentPage === 1}
                  className="page-btn"
                >
                  ‹
                </button>
                
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button 
                  onClick={() => setCurrentPage(currentPage + 1)} 
                  disabled={currentPage === totalPages}
                  className="page-btn"
                >
                  ›
                </button>
                <button 
                  onClick={() => setCurrentPage(totalPages)} 
                  disabled={currentPage === totalPages}
                  className="page-btn"
                >
                  »
                </button>
              </div>
            )}
            
            <div className="table-footer">
              <span>Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCustomers.length)} of {filteredCustomers.length} customers</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCustomers;